import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Paper,
  Typography
} from "@mui/material";
import React from "react";

import { DatasetItem } from "../../../types/dataset";

interface DatasetItemDetailDialogProps {
  open: boolean;
  onClose: () => void;
  item: DatasetItem | null;
}

export const DatasetItemDetailDialog: React.FC<DatasetItemDetailDialogProps> = ({
  open,
  onClose,
  item
}) => {
  if (!item) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getAnnotationTypeColor = (type: string) => {
    switch (type) {
      case "benchmark": return "primary";
      case "issue": return "error";
      case "score": return "success";
      case "routing": return "info";
      case "missing_lens": return "warning";
      default: return "default";
    }
  };

  const formatJson = (obj: any) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Dataset Item Details</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Interaction ID</Typography>
              <Link
                href={`/explore?interaction=${item.interaction_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {item.interaction_id}
              </Link>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Artifact Type</Typography>
              <Chip label={item.artifact_type} size="small" />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Annotation Type</Typography>
              <Chip
                label={item.annotation_type}
                size="small"
                color={getAnnotationTypeColor(item.annotation_type) as any}
              />
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Category</Typography>
            <Typography variant="body1">{item.annotation_category}</Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Description</Typography>
            <Typography variant="body1">{item.annotation_description}</Typography>
          </Box>

          {item.annotation_notes && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Notes</Typography>
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                {item.annotation_notes}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Expected Value
          </Typography>
          <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
            <pre style={{
              margin: 0,
              fontSize: "0.875rem",
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word"
            }}>
              {formatJson(item.expected_value)}
            </pre>
          </Paper>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Artifact Content
          </Typography>
          <Paper sx={{ p: 2, bgcolor: "grey.50", maxHeight: 300, overflow: "auto" }}>
            <pre style={{
              margin: 0,
              fontSize: "0.875rem",
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word"
            }}>
              {formatJson(item.artifact_content)}
            </pre>
          </Paper>
        </Box>

        {item.artifact_config && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Artifact Configuration
              </Typography>
              <Paper sx={{ p: 2, bgcolor: "grey.50", maxHeight: 200, overflow: "auto" }}>
                <pre style={{
                  margin: 0,
                  fontSize: "0.875rem",
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word"
                }}>
                  {formatJson(item.artifact_config)}
                </pre>
              </Paper>
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="h6" gutterBottom>
            Metadata
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Created By</Typography>
              <Typography variant="body1">{item.created_by}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Created</Typography>
              <Typography variant="body1">{formatDate(item.created_at)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Updated</Typography>
              <Typography variant="body1">{formatDate(item.updated_at)}</Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
